<template>
  <div class="new-content-ownership container">
    <BreadcrumbNav :items="breadcrumb" />
    <template v-if="isLoading">
      <ContentBlock :loading="isLoading" />
      <ContentBlock :loading="isLoading" />
    </template>
    <div class="pages" v-else>
      <div class="steps mb6" v-if="!mobileLayout">
        <div
          style="height:10rem;"
          class="step bg-gradient-info flex items-center justify-center f-body"
          v-for="step in steps"
          :key="step.id"
          @click="doGoToStep(step.id)"
          :class="{ '-active': step.id === currentStepId, '-completed': completedSteps.includes(step.id) }"
        >
          <div class="tc">
            <h3 class>Step {{ step.position }}:</h3>
            <h3 class>{{ step.title }}</h3>
          </div>
        </div>
      </div>
      <UISelect
        v-else
        class="mb4"
        :value="currentStepId"
        :placeholder="$t('Select a step')"
        @update-model="id => doGoToStep(id)"
      >
        <option
          v-for="step in steps"
          :key="step.id"
          :value="step.id"
          :disabled="!(step.id === currentStepId || completedSteps.includes(step.id))"
        >Step {{ step.position }}/{{ steps.length }}: {{ step.title }}</option>
      </UISelect>
      <SetupContent v-if="currentStepId === 'content'" :contentOwnership="contentOwnership" />
      <SetupPrice v-if="currentStepId === 'price'" :contentOwnership="contentOwnership" />
      <SetupCDS v-if="currentStepId === 'cds'" :contentOwnership="contentOwnership" />
      <SetupEULA v-if="currentStepId === 'eula'" :contentOwnership="contentOwnership" />
    </div>
    <BottomBar v-if="contentOwnership" :custom="true">
      <UIButton ghost>
        <span class="v-mid dib" @click="doCancel">{{ $t('Cancel') }}</span>
      </UIButton>
      <template #buttons>
        <UIButton ghost>
          <span class="v-mid dib" @click="doSaveAndExit">{{ $t('Save & Exit') }}</span>
        </UIButton>
        <UIButton ghost v-if="completedSteps.length" @click="doGoToPreviousStep">
          <UIIcon class="v-mid mr1" style="width: 1.7rem;" name="chevron-left" />
          <span class="v-mid dib">{{ $t('Previous') }}</span>
        </UIButton>
        <UIButton @click="doSaveAndContinue" :disabled="!(valid && !isLoading)">
          <span class="v-mid dib">{{ continueText }}</span>
          <UIIcon
            v-if="steps.length - 1 !== stepIndex"
            class="v-mid ml1"
            style="width: 1.7rem;"
            name="chevron-right"
          />
        </UIButton>
      </template>
    </BottomBar>
  </div>
</template>

<script>
import { computed, watch, watchEffect } from '@vue/composition-api';
import BreadcrumbNav from '@/core/shared/components/BreadcrumbNav';
import SetupContent from './components/SetupContent';
import SetupPrice from './components/SetupPrice';
import SetupEULA from './components/SetupEULA';
import SetupCDS from './components/SetupCDS';
import FocusModeMixin from '@/core/shared/misc/FocusModeMixin';
import ContentBlock from '@/core/shared/components/ContentBlock';
import store from '@/core/store';
import i18n from '@/core/i18n';
import { useObjectWithChangeDetection, useLoading } from '@/core/shared/composition-helpers';
import { useMQ } from '@/core/shared/compositions/useMQ';
import { usePermissions } from '@/core/shared/setup/permissions';
import router from '@/core/routes';
import BottomBar from '@/core/shared/components/BottomBar';
import UIButton from '@/core/shared/components/ui/UIButton';
import UIIcon from '@/core/shared/components/ui/UIIcon';
import UISelect from '@/core/shared/components/ui/UISelect';
import ContentOwnershipValidatorHelper from '../ContentOwnershipValidatorHelper';

export default {
  name: 'PendingContentOwnershipRoute',
  mixins: [FocusModeMixin],
  components: {
    BreadcrumbNav,
    SetupCDS,
    SetupContent,
    SetupPrice,
    SetupEULA,
    ContentBlock,
    BottomBar,
    UIButton,
    UIIcon,
    UISelect,
  },
  props: {
    contentOwnershipId: {
      type: String,
      required: true,
    },
  },
  setup (props) {
    const AVAILABLE_STEPS = usePermissions()['enterprise-developer'] ? ['content', 'cds'] : ['content', 'price', 'eula', 'cds'];
    const STEPS = {
      content: {
        title: i18n.t('Content Details'),
      },
      price: {
        title: i18n.t('Pricing & Distribution'),
      },
      eula: {
        title: i18n.t('End User License Agreement'),
      },
      cds: {
        title: i18n.t('Content Distribution Upload'),
      },
    };
    const contentOwnershipInStore = computed(() => {
      return store.getters['entities/ContentOwnership'](props.contentOwnershipId);
    });
    const { changes: contentOwnershipChanges, object: contentOwnership } = useObjectWithChangeDetection(contentOwnershipInStore);
    const steps = AVAILABLE_STEPS.map((id, i) => ({
      id,
      position: i + 1,
      ...STEPS[id],
    }));
    const currentStepId = computed(() => {
      return contentOwnership.value?.status || 'content';
    });
    const stepIndex = computed(() => AVAILABLE_STEPS.findIndex((val) => val === currentStepId.value));
    const nextStep = computed(() => stepIndex.value < AVAILABLE_STEPS.length - 1 && AVAILABLE_STEPS[stepIndex.value + 1]);
    const previousStep = computed(() => stepIndex.value > 0 && AVAILABLE_STEPS[stepIndex.value - 1]);
    const completedSteps = computed(() => AVAILABLE_STEPS.slice(0, stepIndex.value));
    const stepText = computed(() => {
      const index = AVAILABLE_STEPS.findIndex((val) => val === currentStepId.value);
      return `${index + 1}/${AVAILABLE_STEPS.length}`;
    });
    const currentStep = computed(() => {
      return STEPS[currentStepId.value];
    });
    function doGoToStep (stepId) {
      if (completedSteps.value.includes(stepId)) {
        contentOwnership.value.status = stepId;
      }
    }
    function doGoToPreviousStep () {
      contentOwnership.value.status = previousStep.value;
    }

    const { loading: isLoading, subscribeToPromise } = useLoading();
    const { mobileLayout } = useMQ();
    const continueText = computed(() => {
      if (stepIndex.value === AVAILABLE_STEPS.length - 1) {
        return mobileLayout ? i18n.t('Publish') : i18n.t('Save & Publish');
      }
      return mobileLayout ? i18n.t('Next') : i18n.t('Next');
    });
    subscribeToPromise(store.dispatch('getContentOwnership', props.contentOwnershipId));
    watchEffect(() => {
      contentOwnership.value && store.commit('setTitle', contentOwnership.value.title);
    });
    watch(() => currentStepId.value, (val) => {
      (function resetScroll () {
        if (window.scrollTo) {
          window.scrollTo(0, 0);
        }
      })();
    });

    async function doSave (override) {
      const contentOwnershipToSave = { id: props.contentOwnershipId, ...contentOwnershipChanges.value, ...override };
      const promiseBefore = Promise.all([
        store.dispatch('createPrices', contentOwnership.value),
        store.dispatch('createContentOwnershipAgreement', contentOwnershipToSave),
      ]);
      const promise = store.dispatch('storeContentOwnership', contentOwnershipToSave);
      subscribeToPromise(promiseBefore);
      subscribeToPromise(promise);
      const isPublishing = contentOwnership.status === 'active';
      try {
        await promiseBefore;
        await promise;
        if (isPublishing) {
          store.commit('setFlash', {
            type: 'success',
            message: i18n.t('Your content is now published.'),
          });
          router.push({ name: 'content-ownerships' });
        }
      } catch (e) {
        isPublishing && store.commit('setFlash', {
          type: 'error',
          message: i18n.t('There was an error publishing your content, please try again.'),
        });
        throw e;
      }
      contentOwnershipChanges.value = {};
    }
    async function doSaveAndExit () {
      await doSave();
      router.push({ name: 'content-ownerships' });
    }
    async function doSaveAndContinue () {
      await doSave({
        status: nextStep.value ? `${nextStep.value}` : 'active',
      });
    }
    function doCancel () {
      router.push({ name: 'content-ownerships' });
    }
    const valid = computed(() => {
      if (!nextStep.value) {
        return store.getters.currentOrganization.taxForm?.status === 'approved' && ContentOwnershipValidatorHelper.valid(contentOwnership.value) && ContentOwnershipValidatorHelper.CDSUploadValidity(contentOwnership.value);
      }
      if (currentStepId.value === 'cds') {
        return ContentOwnershipValidatorHelper.CDSUploadValidity(contentOwnership.value);
      } else if (currentStepId.value === 'content') {
        const basics = ContentOwnershipValidatorHelper.basicValidity(contentOwnership.value);
        const players = ContentOwnershipValidatorHelper.playerValidity(contentOwnership.value);
        return basics && players;
      } else if (currentStepId.value === 'price') {
        return ContentOwnershipValidatorHelper.priceValidity(contentOwnership.value);
      } else if (currentStepId.value === 'eula') {
        return ContentOwnershipValidatorHelper.EULAValidity(contentOwnership.value);
      }
    });

    return {
      isLoading,
      mobileLayout,
      contentOwnership,
      contentOwnershipChanges,
      currentStepId,
      valid,
      stepText,
      steps,
      stepIndex,
      completedSteps,
      currentStep,
      continueText,
      doSaveAndExit,
      doSaveAndContinue,
      doCancel,
      doGoToStep,
      doGoToPreviousStep,
      breadcrumb: [
        {
          name: i18n.t('Content'),
          routerLink: { name: 'content-ownerships' },
        },
        {
          name: i18n.t('Add Content'),
        },
      ],
    };
  },
};
</script>

<style scoped lang="postcss">
@import "core/shared/styles";

.new-content-ownership {
  padding-top: var(--spacingMd);

  & .pages {
    margin-top: var(--spacingMd);
  }
}
.steps {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: var(--spacingMd);
}
.step {
  border-radius: var(--radius);
  color: var(--colorGeyser);
  background: white;
  box-shadow: var(--shadowSm);
  &.-completed {
    /* border: 0.1rem solid var(--colorGeyser); */
    color: var(--colorSlate);
    flex-direction: column;
    &:hover {
      cursor: pointer;
      box-shadow: 0 0.5rem 2rem 0 rgba(0, 0, 0, 0.075);
    }
  }
  &.-active {
    position: relative;
    color: var(--colorSlate);
    font-weight: 600;
    &:before {
      background: var(--colorInfo);
      position: absolute;
      bottom: 0%;
      left: 0%;
      width: 100%;
      height: 0.3em;
      border-radius: 0 0 0.2em 0.2em;
      background: var(--colorInfo);
      content: "";
    }
  }
  /* &.-active {
    @apply --modalDropShadow;

    position: relative;
    background: var(--colorInfo);
    color: white;
    &:before {
      position: absolute;
      bottom: 0%;
      left: 50%;
      border-left: inherit;
      border-bottom: inherit;
      width: 1.7em;
      height: 1.7em;
      border-radius: 0 0 0 0.2em;
      transform: translate(-50%, 50%);
      background: var(--colorInfo);
      content: "";
      clip-path: polygon(100% 50%, 50% 100%, 0% 50%);
    }
  } */
}
</style>

<style lang="postcss">
@import "core/shared/styles";

.update-notification {
  border: 0.1rem solid var(--colorGeyser);
  padding: var(--spacingMd);
  margin-bottom: var(--spacingMd);

  & .f9 {
    margin-bottom: 1.5rem;
    color: var(--colorFjord);
  }

  & ul {
    list-style-type: disc;
    padding-left: var(--spacingMd);
    margin-top: var(--spacingSm);
  }

  & li {
    margin-bottom: var(--spacingSm);
  }

  & i {
    color: var(--colorManatee);
    font-style: italic;
  }
}
</style>
